export enum CertificateDisplayStatusFillColor {
  'active' = '#DCFCE8',
  'sell_offer' = '#CFF7FE',
  'buy_offer' = '#DBE8FE',
  'sold' = '#CFF7FE',
  'gift_sent' = '#F4E8FF',
  'gift_received' = '#FCE7F1',
  'gifted' = '#F4E8FF',
  'stolen' = '#FEE2E2',
  'lost' = '#FFE6D5',
}

export enum CertificateDisplayStatusBorderColor {
  'active' = '#BBF7D1',
  'sell_offer' = '#A5EFFC',
  'buy_offer' = '#BFD7FE',
  'sold' = '#A5EFFC',
  'gift_sent' = '#EBD5FF',
  'gift_received' = '#FBCFE5',
  'gifted' = '#EBD5FF',
  'stolen' = '#FECACA',
  'lost' = '#FECCAA',
}

export enum CertificateDisplayStatusTextColor {
  'active' = '#14532B',
  'sell_offer' = '#165863',
  'buy_offer' = '#1E478A',
  'sold' = '#165863',
  'gift_sent' = '#531C87',
  'gift_received' = '#83184D',
  'gifted' = '#531C87',
  'stolen' = '#7F1D1D',
  'lost' = '#7C3D12',
}

export type CertificateDisplayStatus = keyof typeof CertificateDisplayStatusFillColor;

/** The three tones a status pill needs: pale fill, mid-tone border, deep label. */
export interface CertificateStatusRamp {
  backgroundColor: string;
  borderColor: string;
  color: string;
}

/** Resolve one of the display-status ramps above into a `StatusBadge` `colors` prop. */
const ramp = (status: CertificateDisplayStatus): CertificateStatusRamp => ({
  backgroundColor: CertificateDisplayStatusFillColor[status],
  borderColor: CertificateDisplayStatusBorderColor[status],
  color: CertificateDisplayStatusTextColor[status],
});

/**
 * Neutral zinc, for states that are neither good nor bad news (`created`, `inactive`).
 *
 * Same 50/200/800 zinc the scan chips already use for their neutral outcomes
 * (`packages/ui/utils/scanStatus.ts`, `link_stale`), so the two chip families stay one
 * palette. Lives here rather than being added to the enums above because those are
 * keyed on DISPLAY statuses, a different (and externally-defined) vocabulary.
 */
const NEUTRAL_RAMP: CertificateStatusRamp = {
  backgroundColor: '#F4F4F5',
  borderColor: '#E4E4E7',
  color: '#27272A',
};

export interface CertificateStatusChipMeta {
  /** Short, sentence-case, sized to fit a 375px list row beside a timestamp. */
  label: string;
  colors: CertificateStatusRamp;
}

/**
 * Certificate status → chip label + tint (VERA-978).
 *
 * The Activity timeline shows the state each event left the certificate in, so it
 * needs the RAW certificate statuses — `transfer_waiting_confirmation` and friends —
 * not the display-status vocabulary the enums above are keyed on. Two rules drove the
 * labels:
 *
 * - Never show a raw enum value. `transfer_waiting_confirmation` is a database token,
 *   not English.
 * - Keep them short. The chip shares one line with a relative timestamp inside a
 *   375px row, and every character it takes comes out of the event label's column.
 *
 * Tints reuse the display-status ramps so a status chip in the timeline matches the
 * one in the certificate header: transfers blue, cancellation purple, and the
 * orange/red the app already uses for lost and stolen.
 *
 * `active` and `created` deliberately have NO entry: their own event labels
 * ("Transfer accepted", "Certificate created") already say what happened, so a
 * matching "Active"/"Created" pill only restates the row. Falling through to
 * `getCertificateStatusChip`'s `?? null` makes them behave exactly like the
 * missing-status → no-chip path.
 */
/**
 * VRG-455 — the chip for a purchase the buyer has not claimed yet.
 *
 * A NAMED EXPORT rather than an entry in `CERTIFICATE_STATUS_CHIPS`, and the
 * distinction is real: that map is keyed on raw CERTIFICATE statuses for the
 * Activity timeline, and a pending sale has no certificate at all — that absence
 * is the whole premise of the feature. Filing it there would put a non-status in
 * a status registry and imply the timeline can render it.
 *
 * LABEL IS AN ACTION, not a state, which is deliberate and is the one place this
 * chip departs from its neighbours. The card is tappable and goes straight to
 * the claim, so naming the action is accurate rather than misdirecting. The
 * earlier "Not verified" was worse than merely terse: that chip answers an
 * AUTHENTICITY question ("did a genuine scan get you here"), which is meaningful
 * on the certificate page — reachable by a cloned URL — and meaningless in a
 * list the buyer reached from their own account. It imported a doubt frame onto
 * a row where nothing is in doubt.
 *
 * Sentence case, matching "Awaiting accept" and "Cancel requested" rather than
 * title case.
 *
 * `gift_received`'s ramp because that is the app's existing colour for an
 * incoming thing awaiting your action, and the gift row two cards down already
 * says "Claim it to add it to your certificates" — same vocabulary, so it should
 * carry the same tint.
 */
export const PENDING_OWNERSHIP_CHIP: CertificateStatusChipMeta = {
  label: 'Claim ownership',
  colors: ramp('gift_received'),
};

export const CERTIFICATE_STATUS_CHIPS: Record<string, CertificateStatusChipMeta> = {
  inactive: {label: 'Inactive', colors: NEUTRAL_RAMP},
  // The outgoing-transfer states collapse into one chip. `transfer_initiated` and
  // `transfer_waiting_confirmation` already render byte-identical user-facing copy
  // elsewhere (`StatusNotification.tsx`), so the app has already decided they are one
  // concept — this matches it. Also covers gift/sale transfers, since neither is a
  // distinct backend state (a gift is a regular transfer under the hood). Blue,
  // matching `buy_offer`.
  transfer_initiated: {label: 'Transfer', colors: ramp('buy_offer')},
  transfer_waiting_confirmation: {label: 'Transfer', colors: ramp('buy_offer')},
  waiting_accept: {label: 'Awaiting accept', colors: ramp('sell_offer')},
  cancel_request: {label: 'Cancel requested', colors: ramp('gift_sent')},
  lost: {label: 'Lost', colors: ramp('lost')},
  stolen: {label: 'Stolen', colors: ramp('stolen')},
};

/**
 * Chip metadata for a certificate status, or `null` when there is nothing to show.
 *
 * Returns null for BOTH a missing status and an unrecognised one. Most action-log rows
 * carry no `snapshot.status` at all (only some of the writers record it — backfill is
 * tracked as VRG-196), so absence is the common case, and the correct rendering of
 * "we don't know" is no chip rather than an "Unknown" pill.
 */
export const getCertificateStatusChip = (
  status?: string | null,
): CertificateStatusChipMeta | null => (status ? CERTIFICATE_STATUS_CHIPS[status] ?? null : null);

/** `transfer_waiting_confirmation` -> `Transfer waiting confirmation`. */
const humanizeStatus = (status: string): string =>
  status.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());

/**
 * VRG-511 — the certificate status badge has TWO tiers, not five hues.
 *
 * ## Why two
 *
 * Five colours is a legend nobody learns. The question a reader is actually asking
 * down a list is *is something happening* versus *is something wrong*, and that is
 * readable without decoding. Collapsing to two also means a genuinely serious state
 * registers instead of competing with four equally-coloured neighbours — give every
 * state its own alarm and the serious ones stop registering at all.
 *
 * ## Tinted, never filled (§32)
 *
 * Pale fill, deep text. A list is eight cards deep, and a saturated fill repeated
 * eight times is a pre-attentive pop-out that inverts the hierarchy: the product name
 * should lead, not its status. §32's rule — persistent chrome must not be loud — has
 * no more literal application than a list row.
 *
 * Measured text-on-fill contrast, both tiers comfortably past AA's 4.5:1:
 *   neutral  #27272A on #F4F4F5 = 13.55:1
 *   warning  #7F1D1D on #FEE2E2 =  8.20:1
 *
 * ## `active` is absent on purpose — see `getCertificateStatusBadgeProps`
 */
const WARNING_RAMP: CertificateStatusRamp = {
  backgroundColor: '#FEE2E2',
  borderColor: '#FECACA',
  color: '#7F1D1D',
};

/**
 * The states that mean SOMETHING IS WRONG. Everything else that shows a badge at all
 * is "in flight, not wrong" and takes the neutral zinc.
 *
 * Measured against the seed data, this split is not only a severity judgement — it
 * also lands exactly where the badge is doing WORK. Of the non-active certificates a
 * customer holds, 8 of 10 carry an activity line directly beneath that already
 * explains the status ("Gift sent to @x. Not claimed yet" under a `Transfer` pill), and
 * every one of those 8 is neutral-tier. Neither warning-tier row is explained by its
 * line: a stolen or lost item's activity row says "Last scanned …", which names the
 * scan and never the problem. So the tier that reads loudest is also the tier carrying
 * information nothing else on the row carries.
 */
const WARNING_STATUSES = new Set(['stolen', 'lost']);

export interface CertificateStatusBadgeProps {
  /** False for the states that carry no information and should render nothing. */
  show: boolean;
  label: string;
  /** Undefined lets StatusBadge fall back to its own display-status colour maps. */
  colors?: CertificateStatusRamp;
}

/**
 * Everything a status badge needs, for every surface that shows one.
 *
 * Extracted rather than copied (§36): the list card and the certificate page both
 * rendered `<StatusBadge status={raw} />`, which prints `label ?? status` — so any
 * state the colour maps didn't cover shipped the database token to the customer
 * verbatim ("Transfer_waiting_confirmation"). Fixing that on the card alone left
 * the identical bug on the certificate page, which is exactly the drift a second
 * hand-roll causes.
 *
 * Two rules, held in one place:
 * - `active` renders nothing. It is the default state and says nothing (VERA-628).
 * - A status the CHIPS map doesn't cover falls back to a HUMANISED label, never to
 *   the raw token. The map is keyed to the Activity timeline's vocabulary and does
 *   not include every certificate state (`in_transfer`, `returned`, ...), so
 *   without this those states would either leak schema or silently vanish.
 */
export const getCertificateStatusBadgeProps = (
  status?: string | null,
): CertificateStatusBadgeProps => {
  /*
    `active` RENDERS NOTHING, and this is the load-bearing half of the treatment
    rather than a tidy-up.
  
    It is the default state and the majority — measured on the seed data, 59.2% of
    non-archived certificates and 58.3% of the ones a customer actually holds. A badge
    on six rows in ten says nothing that the absence of any other badge would not say,
    and a marker repeated down most of a list stops being read at all: it becomes
    furniture the eye skips, which is precisely how the old status DOT came to read as
    "hard to read". Removing it is the same move as dropping the "Not verified" badge
    from tags that can never verify, and the carousel dot when there is one image.
  
    (This behaviour predates VRG-511 — the `active` early-return was already here. It
    is documented now because the two-tier work below makes it a deliberate part of a
    treatment rather than an isolated special case.)
  */
  if (!status || status === 'active') return { show: false, label: '' };

  const chip = getCertificateStatusChip(status);
  /*
    VRG-511 — the LABEL still comes from the per-status map (so "Transfer",
    "Cancel requested", "Stolen" all keep their own words), but the TINT no longer
    does: it collapses to one of two tiers. See WARNING_RAMP above for why two.
  
    The colour is resolved HERE rather than by editing `CERTIFICATE_STATUS_CHIPS`
    itself, deliberately. That map is also read by the Activity timeline
    (`getCertificateStatusChip`), which answers a different question — "what state did
    this EVENT leave the certificate in", one row per event, where the five hues are
    doing real work distinguishing adjacent entries. Rewriting the shared map would
    have changed that surface as a side effect of a list-row decision nobody made about
    it. The divergence is intentional and is flagged for review rather than taken
    silently.
  */
  const colors = WARNING_STATUSES.has(status) ? WARNING_RAMP : NEUTRAL_RAMP;
  return { show: true, label: chip?.label ?? humanizeStatus(status), colors };
};
